import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router';
import history from '../History';

/**
 * withRouter compat shim for class components.
 * Injects history, location, and match props using React Router v7 hooks.
 * Adapts history v5 API back to v4 signatures for block() and listen().
 */
export function withRouter(Component) {
    function WithRouter(props) {
        const navigate = useNavigate();
        const location = useLocation();
        const params = useParams();

        const historyCompat = {
            push: navigate,
            replace: to => navigate(to, { replace: true }),
            go: n => navigate(n),
            goBack: () => navigate(-1),
            goForward: () => navigate(1),
            location,

            // Adapt history v5 listen() to v4 signature: v4 (location, action) vs v5 ({ action, location })
            listen: callback => history.listen(({ action, location }) => callback(location, action)),

            // Adapt history v5 block() to v4 behavior:
            // v4: callback(location, action) returns string (show prompt) or falsy (allow)
            // v5: callback({ action, location, retry }) — hard blocks, must call retry() to proceed
            block: callback => {
                let unblock;
                const arm = () => {
                    unblock = history.block(({ action, location, retry }) => {
                        const result = callback(location, action);
                        if (!result || window.confirm(result)) {
                            unblock(); // stop blocking
                            const stop = history.listen(() => {
                                // re-arm AFTER nav applies
                                stop();
                                arm();
                            });
                            retry();
                        }
                        // else: stay (history v5 already reverted)
                    });
                };
                arm();
                return () => unblock();
            }
        };

        const match = {
            params,
            path: location.pathname,
            url: location.pathname
        };

        return <Component {...props} history={historyCompat} location={location} match={match} />;
    }

    WithRouter.displayName = `withRouter(${Component.displayName || Component.name || 'Component'})`;
    return WithRouter;
}
